import { createContext, useContext, useEffect, useRef, useState } from 'react'
import type { DragEvent, PointerEvent as ReactPointerEvent, ReactNode } from 'react'

import { t } from '../i18n/lingua'

/**
 * Il titolo scelto dalla persona per un widget («Uplink verso il DC»): vince
 * su quello del catalogo, che scende a sottotitolo. Lo fornisce chi disegna
 * il widget (`DisegnaWidget`), lo legge il riquadro: così vale per tutti i
 * widget senza che ognuno lo sappia.
 */
export const TitoloWidgetContext = createContext<string | undefined>(undefined)

/**
 * True on a wall display: the widgets' small «apri» / «report» links in the
 * header are hidden — noise on a monitor, and from a monitor's link they
 * would lead nowhere. Provided by the wall pages around their grid.
 */
export const SinotticoContext = createContext(false)

/**
 * La griglia dei widget della dashboard personalizzabile, in due modi.
 *
 * **In fila** (il predefinito): dodici colonne, come le griglie di stampa;
 * ogni widget occupa una **misura** (¼, ⅓, ½, ⅔, ¾ di riga, riga intera) e
 * una o due righe; i widget si impilano nell'ordine scelto, si trascinano
 * per riordinarli, e la griglia li fa tornare su qualsiasi schermo, anche su
 * un telefono dove le colonne diventano una sola.
 *
 * **Libera** (`libero`): una matrice di 24 colonne per 12 righe — uno
 * schermo — dove ogni widget ha la sua posizione e la sua misura in celle
 * (`grid: {x, y, w, h}`), si sposta trascinandolo dalla maniglia e si
 * ridimensiona dall'angolo in basso a destra; i widget possono anche
 * sovrapporsi (un testo sopra una planimetria), l'ultimo sta sopra. È il
 * modo per comporre un sinottico «come si vuole»: sul monitor le dodici
 * righe riempiono lo schermo, nell'editor la matrice ha le proporzioni 16:9.
 */

/** Widths of a row: a quarter, a third, a half, two thirds, three quarters, the whole row. */
export type MisuraWidget = 's' | 'm' | 'l' | 'l2' | 'l3' | 'xl'

/** A place on the free matrix: cell coordinates and size (24 × 12 = one screen). */
export interface PosizioneGriglia {
  x: number
  y: number
  w: number
  h: number
}

export interface WidgetInGriglia {
  id: string
  size: MisuraWidget
  /** Alto: due righe invece di una (grafici, elenchi lunghi). */
  tall?: boolean
  /** On the free matrix: where and how big. Missing = placed after the others. */
  grid?: PosizioneGriglia
}

export const MISURE: { key: MisuraWidget; label: () => string; colonne: number }[] = [
  { key: 's', label: () => t('¼ di riga'), colonne: 3 },
  { key: 'm', label: () => t('⅓ di riga'), colonne: 4 },
  { key: 'l', label: () => t('½ riga'), colonne: 6 },
  { key: 'l2', label: () => t('⅔ di riga'), colonne: 8 },
  { key: 'l3', label: () => t('¾ di riga'), colonne: 9 },
  { key: 'xl', label: () => t('riga intera'), colonne: 12 },
]

/** The free matrix: columns and the rows of one screen. */
export const COLONNE_LIBERE = 24
export const RIGHE_SCHERMO = 12
const RIGHE_MASSIME = 96
const GAP = 14

/**
 * Every widget with a place on the matrix: the ones that have none are
 * packed after the others, left to right, with a size that mirrors their
 * row measure (½ row → 12 cells wide, «alto» → 4 rows). Used when a section
 * turns free and whenever a widget comes in without a position.
 */
export function completaGriglia<W extends WidgetInGriglia>(widgets: W[]): W[] {
  const piazzati = widgets.filter((w) => w.grid)
  let fondo = piazzati.reduce((m, w) => Math.max(m, w.grid!.y + w.grid!.h), 0)
  let x = 0
  let rigaH = 0
  return widgets.map((w) => {
    if (w.grid) return w
    const colonne = MISURE.find((m) => m.key === w.size)?.colonne ?? 4
    const larg = Math.min(COLONNE_LIBERE, colonne * 2)
    const alt = w.tall ? 4 : 3
    if (x + larg > COLONNE_LIBERE) {
      x = 0
      fondo += rigaH
      rigaH = 0
    }
    const grid = { x, y: fondo, w: larg, h: alt }
    x += larg
    rigaH = Math.max(rigaH, alt)
    return { ...w, grid }
  })
}

function limita(g: PosizioneGriglia): PosizioneGriglia {
  const w = Math.max(2, Math.min(COLONNE_LIBERE, Math.round(g.w)))
  const h = Math.max(1, Math.min(RIGHE_MASSIME, Math.round(g.h)))
  const x = Math.max(0, Math.min(COLONNE_LIBERE - w, Math.round(g.x)))
  const y = Math.max(0, Math.min(RIGHE_MASSIME - h, Math.round(g.y)))
  return { x, y, w, h }
}

/** The box's size, from a ResizeObserver: the free matrix needs it to size its cells. */
function useMisura<T extends HTMLElement>(): [React.RefObject<T>, number, number] {
  const ref = useRef<T>(null)
  const [misura, setMisura] = useState<[number, number]>([0, 0])
  useEffect(() => {
    const el = ref.current
    if (!el || typeof ResizeObserver === 'undefined') return
    const ro = new ResizeObserver((entries) => {
      const r = entries[0]?.contentRect
      if (r) setMisura([Math.round(r.width), Math.round(r.height)])
    })
    ro.observe(el)
    return () => ro.disconnect()
  }, [])
  return [ref, misura[0], misura[1]]
}

export function Griglia<W extends WidgetInGriglia>({
  widgets, modifica, onCambia, disegna, libero = false, altezzaRiga, scala = 1,
}: {
  widgets: W[]
  modifica: boolean
  onCambia: (widgets: W[]) => void
  disegna: (w: W) => ReactNode
  /** The free matrix instead of the rows. */
  libero?: boolean
  /** On the free matrix: the height of a row in px (the wall gives one screen / 12); default from the width, 16:9. */
  altezzaRiga?: number
  /** Drawn inside a scaled frame (the reference screen): pointer distances are divided by it. */
  scala?: number
}) {
  const [trascinato, setTrascinato] = useState<string | null>(null)
  const [sopra, setSopra] = useState<string | null>(null)
  const sposta = (daId: string, aId: string) => {
    if (daId === aId) return
    const da = widgets.findIndex((w) => w.id === daId)
    const a = widgets.findIndex((w) => w.id === aId)
    if (da < 0 || a < 0) return
    const nuovi = [...widgets]
    const [voce] = nuovi.splice(da, 1)
    nuovi.splice(a, 0, voce)
    onCambia(nuovi)
  }
  const cambia = (id: string, parte: Partial<WidgetInGriglia>) =>
    onCambia(widgets.map((w) => (w.id === id ? { ...w, ...parte } : w)))

  // --- the free matrix: cell size, pointer-driven move and resize
  const [riquadro, larghezza] = useMisura<HTMLDivElement>()
  const cellaW = larghezza > 0 ? (larghezza - GAP * (COLONNE_LIBERE - 1)) / COLONNE_LIBERE : 40
  const rigaH = altezzaRiga && altezzaRiga > 0
    ? altezzaRiga
    : Math.max(28, Math.round(((larghezza * 9) / 16 - GAP * (RIGHE_SCHERMO - 1)) / RIGHE_SCHERMO))
  const gesto = useRef<{ id: string; cosa: 'sposta' | 'ridimensiona'; x0: number; y0: number; da: PosizioneGriglia } | null>(null)
  const inizia = (e: ReactPointerEvent<HTMLElement>, id: string, cosa: 'sposta' | 'ridimensiona', da: PosizioneGriglia) => {
    if (!modifica) return
    e.preventDefault()
    e.currentTarget.setPointerCapture(e.pointerId)
    gesto.current = { id, cosa, x0: e.clientX, y0: e.clientY, da }
  }
  const muovi = (e: ReactPointerEvent<HTMLElement>) => {
    const g = gesto.current
    if (!g) return
    const k = scala > 0 ? scala : 1
    const dx = Math.round((e.clientX - g.x0) / k / (cellaW + GAP))
    const dy = Math.round((e.clientY - g.y0) / k / (rigaH + GAP))
    const nuova = g.cosa === 'sposta'
      ? limita({ ...g.da, x: g.da.x + dx, y: g.da.y + dy })
      : limita({ ...g.da, w: g.da.w + dx, h: g.da.h + dy })
    const attuale = widgets.find((w) => w.id === g.id)?.grid
    if (attuale && attuale.x === nuova.x && attuale.y === nuova.y && attuale.w === nuova.w && attuale.h === nuova.h) return
    cambia(g.id, { grid: nuova })
  }
  const fine = (e: ReactPointerEvent<HTMLElement>) => {
    if (!gesto.current) return
    try { e.currentTarget.releasePointerCapture(e.pointerId) } catch { /* already released */ }
    gesto.current = null
  }

  const voci = libero ? completaGriglia(widgets) : widgets
  const righe = libero
    ? Math.max(RIGHE_SCHERMO, ...voci.map((w) => w.grid!.y + w.grid!.h))
    : 0
  const stileGriglia = libero ? {
    gridTemplateColumns: `repeat(${COLONNE_LIBERE}, minmax(0, 1fr))`,
    gridTemplateRows: `repeat(${righe}, ${rigaH}px)`,
    gridAutoRows: `${rigaH}px`,
  } : undefined

  return (
    <div ref={riquadro} className={`griglia${modifica ? ' griglia--modifica' : ''}${libero ? ' griglia--libera' : ''}`} style={stileGriglia}>
      {libero && modifica && righe > RIGHE_SCHERMO && (
        // Where the screen ends: what is below the dashed line scrolls, and a
        // wall does not scroll.
        <div className="griglia__schermo" style={{ top: RIGHE_SCHERMO * rigaH + (RIGHE_SCHERMO - 1) * GAP + GAP / 2 }} aria-hidden="true">
          <span>{t('fine dello schermo')}</span>
        </div>
      )}
      {voci.map((w) => {
        const g = w.grid
        const stile = libero && g
          ? { gridColumn: `${g.x + 1} / span ${g.w}`, gridRow: `${g.y + 1} / span ${g.h}` }
          : undefined
        return (
          <div
            key={w.id}
            className={`griglia__cella${libero ? ' griglia__cella--libera' : ` griglia__cella--${w.size}${w.tall ? ' griglia__cella--alta' : ''}`}${sopra === w.id && trascinato !== w.id ? ' griglia__cella--sopra' : ''}${trascinato === w.id ? ' griglia__cella--trascinata' : ''}`}
            style={stile}
            draggable={modifica && !libero}
            onDragStart={(e: DragEvent) => {
              if (!modifica || libero) return
              setTrascinato(w.id)
              e.dataTransfer.effectAllowed = 'move'
              e.dataTransfer.setData('text/plain', w.id)
            }}
            onDragOver={(e: DragEvent) => {
              if (!modifica || libero || !trascinato) return
              e.preventDefault()
              e.dataTransfer.dropEffect = 'move'
              if (sopra !== w.id) setSopra(w.id)
            }}
            onDragLeave={() => { if (sopra === w.id) setSopra(null) }}
            onDrop={(e: DragEvent) => {
              if (libero) return
              e.preventDefault()
              const da = e.dataTransfer.getData('text/plain') || trascinato
              if (da) sposta(da, w.id)
              setTrascinato(null)
              setSopra(null)
            }}
            onDragEnd={() => { setTrascinato(null); setSopra(null) }}
          >
            {modifica && (
              <div className="griglia__strumenti">
                {libero && g ? (
                  <span className="griglia__maniglia griglia__maniglia--libera" title={t('Trascina per spostare')} aria-hidden="true"
                        onPointerDown={(e) => inizia(e, w.id, 'sposta', g)} onPointerMove={muovi} onPointerUp={fine} onPointerCancel={fine}>⋮⋮</span>
                ) : (
                  <span className="griglia__maniglia" title={t('Trascina per spostare')} aria-hidden="true">⋮⋮</span>
                )}
                {libero && g ? (
                  <span className="muted eagle-piccolo" title={t('Posizione e misura in celle (24 colonne × 12 righe = uno schermo)')}>{g.w}×{g.h}</span>
                ) : (
                  <>
                    <select value={w.size} aria-label={t('Misura del widget')}
                            onChange={(e) => cambia(w.id, { size: e.target.value as MisuraWidget })}>
                      {MISURE.map((m) => <option key={m.key} value={m.key}>{m.label()}</option>)}
                    </select>
                    <label className="griglia__alto">
                      <input type="checkbox" checked={!!w.tall} onChange={(e) => cambia(w.id, { tall: e.target.checked })} />
                      {' '}{t('alto')}
                    </label>
                  </>
                )}
                <button type="button" className="linklike" onClick={() => onCambia(widgets.filter((x) => x.id !== w.id))}>
                  {t('togli')}
                </button>
              </div>
            )}
            {disegna(w)}
            {modifica && libero && g && (
              <span className="griglia__ridimensiona" title={t('Trascina per ridimensionare')} aria-hidden="true"
                    onPointerDown={(e) => inizia(e, w.id, 'ridimensiona', g)} onPointerMove={muovi} onPointerUp={fine} onPointerCancel={fine} />
            )}
          </div>
        )
      })}
    </div>
  )
}

/**
 * Il riquadro di un widget: titolo, sottotitolo, contenuto. Mentre ricarica il
 * contenuto resta, un po' sbiadito: niente salti, niente lampi. `nudo` toglie
 * la cornice e la testata: un'immagine o una scritta su un sinottico non
 * vogliono un riquadro attorno.
 */
export function Widget({
  titolo, sotto, azioni, caricando, errore, children, link, nudo = false,
}: {
  titolo: string
  sotto?: string
  azioni?: ReactNode
  caricando?: boolean
  errore?: unknown
  children: ReactNode
  link?: ReactNode
  nudo?: boolean
}) {
  const scelto = useContext(TitoloWidgetContext)?.trim()
  const sulSinottico = useContext(SinotticoContext)
  // Dim only while the first data is on its way. Every refresh after that
  // (the widgets poll each minute) keeps the drawing as it is: a wall of
  // widgets going pale together every minute reads as a blink.
  const [pronto, setPronto] = useState(false)
  useEffect(() => { if (!caricando) setPronto(true) }, [caricando])
  if (nudo) {
    return <section className="widget widget--nudo" aria-label={scelto || titolo}>{children}</section>
  }
  return (
    <section className="widget" aria-busy={caricando || undefined}>
      <header className="widget__testa">
        <div>
          <h3 className="widget__titolo">{scelto || titolo}</h3>
          {sotto ? <div className="widget__sotto muted">{sotto}</div>
            : scelto ? <div className="widget__sotto muted">{titolo}</div> : null}
        </div>
        <div className="widget__azioni">{azioni}{!sulSinottico && link}</div>
      </header>
      <div className="widget__corpo" style={{ opacity: caricando && !pronto ? 0.6 : 1 }}>
        {errore ? <p className="eagle-motivo" style={{ margin: 0 }}>{t('Dati non disponibili al momento.')}</p> : children}
      </div>
    </section>
  )
}
